import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  School,
  Timer,
  EmojiEvents,
  PlayArrow,
  Person,
  Assessment,
  Refresh,
  History,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TestHistoryComponent from '../components/TestHistoryComponent';
import SubscriptionPlans from '../components/SubscriptionPlans';
import subscriptionService, { SubscriptionStatus } from '../services/subscriptionService';

const DashboardContainer = styled(Box)`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem 0;
`;

const ActionButton = styled(Button)`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const DashboardPage: React.FC = () => {
  const { student, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [greeting, setGreeting] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('apex_auth_token');
      const response = await fetch('/api/auth/dashboard-stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data.data.student.stats);
        // Also refresh the student data in auth context
        await refreshProfile();
      } else {
        console.error('Failed to fetch dashboard stats');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 17) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, []);

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      const status = await subscriptionService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // Handle subscription change
  const handleSubscriptionChange = async () => {
    await fetchSubscriptionStatus();
    setShowSubscriptionPlans(false);
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchSubscriptionStatus();
    
    // Listen for stats updates from other components
    const handleStatsUpdate = () => {
      console.log('📊 Stats update event received, refreshing dashboard...');
      fetchDashboardStats();
    };
    
    window.addEventListener('statsUpdated', handleStatsUpdate);
    
    return () => {
      window.removeEventListener('statsUpdated', handleStatsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!student || loading) {
    return (
      <DashboardContainer>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography variant="h6">Loading...</Typography>
          </Box>
        </Container>
      </DashboardContainer>
    );
  }

  // Use fresh dashboard stats if available, otherwise fall back to student stats
  const stats = dashboardStats || student.stats || {
    totalTests: 0,
    averageScore: 0,
    totalTimeSpent: 0,
    streak: 0,
    bestScore: 0,
    strongSubjects: [],
    weakSubjects: [],
  };

  const MetricCard = ({ title, value, icon, color, subtitle }: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          border: `1px solid ${alpha(color, 0.1)}`,
          transition: 'all 0.3s ease',
          height: '100%',
          minHeight: 120,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
          },
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color, mb: 1 }}>
                  {value}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(color, 0.1),
                color,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <DashboardContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
      <Container maxWidth="lg">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              transition: 'all 0.3s ease',
              mb: 4,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontSize: '1.4rem',
                      mr: 3,
                    }}
                  >
                    {student.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {greeting}, {student.name.split(' ')[0]}!
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      Grade {student.grade} • {student.school}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                      Ready to challenge yourself with today's Olympiad practice?
                    </Typography>
                    {subscriptionStatus && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: subscriptionService.getPlanBadgeColor(subscriptionStatus.plan),
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {subscriptionService.getPlanDisplayName(subscriptionStatus.plan)}
                        </Box>
                        {subscriptionStatus.plan === 'free' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              px: 1.5,
                              py: 0.5
                            }}
                            onClick={() => setShowSubscriptionPlans(true)}
                          >
                            Upgrade
                          </Button>
                        )}
                        {subscriptionStatus.plan !== 'free' && subscriptionStatus.endDate && (
                          <Typography variant="caption" color="text.secondary">
                            Valid until {new Date(subscriptionStatus.endDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <ActionButton
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={() => navigate('/test')}
                >
                  Start Test
                </ActionButton>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Dashboard Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab 
              icon={<Assessment />} 
              label="Overview" 
              id="dashboard-tab-0"
              aria-controls="dashboard-tabpanel-0"
            />
            <Tab 
              icon={<History />} 
              label="Test History" 
              id="dashboard-tab-1"
              aria-controls="dashboard-tabpanel-1"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Stats Header with Refresh Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              Your Statistics
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboardStats}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Box>

        {/* Stats Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
          <MetricCard
            title="Tests Completed"
            value={stats.totalTests}
            icon={<School />}
            color={theme.palette.primary.main}
            subtitle="Total attempts"
          />
          <MetricCard
            title="Average Score"
            value={`${stats.averageScore.toFixed(1)}%`}
            icon={<TrendingUp />}
            color={theme.palette.success.main}
            subtitle="Overall performance"
          />
          <MetricCard
            title="Study Time"
            value={`${Math.round(stats.totalTimeSpent / 60)}h`}
            icon={<Timer />}
            color={theme.palette.warning.main}
            subtitle="Total learning time"
          />
          <MetricCard
            title="Best Score"
            value={`${stats.bestScore}%`}
            icon={<EmojiEvents />}
            color={theme.palette.secondary.main}
            subtitle="Personal best"
          />
        </Box>

        {/* Progress and Quick Actions */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          {/* Progress Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.main}05 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                transition: 'all 0.3s ease',
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.15)}`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Your Progress
                </Typography>
                
                {stats.totalTests > 0 ? (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Overall Performance
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {stats.averageScore.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.averageScore} 
                      sx={{ height: 8, borderRadius: 4, mb: 3 }}
                    />

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Current Streak
                      </Typography>
                      <Chip 
                        label={`${stats.streak} ${stats.streak === 1 ? 'day' : 'days'}`} 
                        color="primary" 
                        size="small"
                        onClick={() => {
                          // Show streak info or do nothing - prevents onClick error
                          console.log('Current streak:', stats.streak, 'days');
                        }}
                      />
                    </Box>

                    {stats.strongSubjects && stats.strongSubjects.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Strong Subjects
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {stats.strongSubjects.map((subject: string) => (
                            <Chip 
                              key={subject}
                              label={subject.toUpperCase()} 
                              color="success" 
                              size="small"
                              onClick={() => {
                                // Navigate to subject-specific history or do nothing
                                console.log('Strong subject:', subject);
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Assessment sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      No tests completed yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Take your first test to see your progress here!
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.secondary.main}05 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                transition: 'all 0.3s ease',
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Quick Actions
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PlayArrow />}
                    onClick={() => navigate('/test')}
                    sx={{ py: 1.2, borderRadius: 2 }}
                  >
                    Take Practice Test
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Person />}
                    onClick={() => navigate('/profile')}
                    sx={{ py: 1.2, borderRadius: 2 }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TestHistoryComponent />
        </TabPanel>
      </Container>

      {/* Subscription Plans Dialog */}
      <SubscriptionPlans
        open={showSubscriptionPlans}
        onClose={() => setShowSubscriptionPlans(false)}
        currentSubscription={subscriptionStatus || undefined}
        onSubscriptionChange={handleSubscriptionChange}
      />
    </DashboardContainer>
  );
};

export default DashboardPage; 